import { classNames, useEffect, useRef, useState } from 'libs/react';
import { React } from 'libs/react';
import { SaleItemCard } from './components/SaleItemCard';
import { StoreSaleItemCardSkeleton } from 'modules/public/store/components/StoreSaleItemCardSkeleton';
import { FormInput } from 'components/form/FormInput';
import { useDomain } from 'components/domain/DomainProvider';
import './DynamicSalesFormProduct.scss';
/**
 * Placeholders shown while the catalog loads: two rows on a desktop, enough
 * to fill a phone screen.
 * @const SKELETON_KEYS
 * @hidden
 */
const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e', 'f'];
/**
 * Finds the product for the ?product= SKU. The SKU can be the product's own
 * or one of its active variants'. A product SKU wins, as it did before.
 *
 * @function findSaleItemBySku
 * @since 1.0.0
 */
function findSaleItemBySku(saleItems, sku) {
    const matches = (value) => (value === null || value === void 0 ? void 0 : value.toLowerCase().trim()) === sku;
    const variantOf = (saleItem) => {
        var _a, _b;
        return (_b = ((_a = saleItem.variants) !== null && _a !== void 0 ? _a : []).find((variant) => variant.active && matches(variant.sku))) !== null && _b !== void 0 ? _b : null;
    };
    const saleItem = saleItems.find((saleItem) => matches(saleItem.sku));
    if (saleItem) {
        return { saleItem, variant: variantOf(saleItem) };
    }
    for (const saleItem of saleItems) {
        const variant = variantOf(saleItem);
        if (variant) {
            return { saleItem, variant };
        }
    }
    return null;
}
/**
 * @component ProductSection
 * @since 1.0.0
 */
export function ProductSection(props) {
    var _a;
    const { items, skuItems, product, autoSelectSingle, hasDirectSales, lang, ready } = props;
    const { options } = useDomain();
    const [selectedItems, setSelectedItems] = useState([]);
    const [presetVariant, setPresetVariant] = useState(null);
    const [saleItems, setSaleItems] = useState([]);
    const productPreselectedRef = useRef(false);
    useEffect(() => {
        if (saleItems.length > 0) {
            // Add event to Google Tag Manager
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'view_item_list',
                ecommerce: {
                    item_list_name: 'saleItems',
                    item_list_id: 'saleItems',
                    items: saleItems.map((saleItem) => ({
                        currency: 'CAD',
                        item_id: saleItem.sku,
                        item_name: saleItem.title,
                        price: saleItem.price || 0,
                        quantity: 1
                    }))
                }
            });
        }
    }, [saleItems, options.gtmId]);
    /**
     * @function variantOf
     * @since 1.0.0
     */
    const variantOf = (id) => (presetVariant === null || presetVariant === void 0 ? void 0 : presetVariant.itemId) === id ? presetVariant.variant : null;
    /**
     * @function onSaleItemCardClick
     * @since 1.0.0
     */
    const onSaleItemCardClick = (id, quantity) => {
        const index = selectedItems.findIndex((item) => item.id === id);
        const variant = variantOf(id);
        const selectedItem = Object.assign({ id,
            quantity }, (variant && { variant: variant.id }));
        let newSelectedItems;
        if (options.selectionMode === 'single') {
            // Single selection mode: replace selection instead of adding
            if (index === -1 && quantity > 0) {
                // Item not selected, replace current selection with this one
                newSelectedItems = [selectedItem];
            }
            else {
                // Item already selected, deselect it
                newSelectedItems = [];
            }
        }
        else {
            // Multiple selection mode: add/remove from list
            if (index === -1 && quantity > 0) {
                newSelectedItems = [...selectedItems, selectedItem];
            }
            else {
                newSelectedItems = selectedItems.filter((item) => item.id !== id);
            }
        }
        // Add event to Google Tag Manager only when selecting (not deselecting)
        if (newSelectedItems.length > 0 &&
            (index === -1 || options.selectionMode === 'single') &&
            quantity > 0) {
            window.dataLayer = window.dataLayer || [];
            const item = saleItems.find((saleItem) => saleItem.id === id);
            const price = (item === null || item === void 0 ? void 0 : item.price) || 0;
            const gtmItems = [
                Object.assign(Object.assign({ currency: 'CAD', item_id: (item === null || item === void 0 ? void 0 : item.sku) || id, item_name: (item === null || item === void 0 ? void 0 : item.title) || '' }, (variant && { item_variant: variant.sku })), { price,
                    quantity })
            ];
            for (const eventName of ['view_item', 'select_item', 'add_to_cart']) {
                window.dataLayer.push({
                    event: eventName,
                    ecommerce: {
                        currency: 'CAD',
                        value: price * quantity,
                        items: gtmItems
                    }
                });
            }
        }
        setSelectedItems(newSelectedItems);
        // check if all selected items are finished products (use newSelectedItems)
        if (newSelectedItems.length > 0) {
            props.setHasOnlyFinishedProducts(saleItems
                .filter((saleItem) => newSelectedItems.some((item) => item.id === saleItem.id))
                .every((saleItem) => saleItem.configuration === 'FINISHED'));
        }
        else {
            props.setHasOnlyFinishedProducts(false);
        }
    };
    useEffect(() => {
        // The list is already filtered by category on the server
        const list = items !== null && items !== void 0 ? items : [];
        if (product) {
            // find the product (or the product of the variant) and select it
            const match = findSaleItemBySku(skuItems !== null && skuItems !== void 0 ? skuItems : [], product);
            if (match) {
                const { saleItem, variant } = match;
                const selectedItem = Object.assign({ id: saleItem.id, quantity: 1 }, (variant && { variant: variant.id }));
                setPresetVariant(variant ? { itemId: saleItem.id, variant } : null);
                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                    event: 'view_item',
                    ecommerce: {
                        currency: 'CAD',
                        value: saleItem.price || 0,
                        items: [
                            Object.assign(Object.assign({ currency: 'CAD', item_id: saleItem.sku, item_name: saleItem.title }, (variant && { item_variant: variant.sku })), { price: saleItem.price || 0, quantity: 1 })
                        ]
                    }
                });
                // Selected once: a removed product stays removed when the list
                // comes back from the network.
                const preselect = !productPreselectedRef.current;
                productPreselectedRef.current = true;
                if (options.selectionMode === 'single' ||
                    options.allowSingleFallback === 'TRUE') {
                    if (preselect) {
                        setSelectedItems([selectedItem]);
                        props.setHasOnlyFinishedProducts(saleItem.configuration === 'FINISHED');
                    }
                    setSaleItems([saleItem]);
                }
                else {
                    if (preselect) {
                        setSelectedItems((selectedItems) => [
                            ...selectedItems.filter((item) => item.id !== saleItem.id),
                            selectedItem
                        ]);
                    }
                    // the product stays visible even outside the default categories
                    setSaleItems(list.some((item) => item.id === saleItem.id)
                        ? list
                        : [saleItem, ...list]);
                }
                return;
            }
        }
        if (!product && autoSelectSingle && list.length === 1) {
            setSelectedItems([{ id: list[0].id, quantity: 1 }]);
            props.setHasOnlyFinishedProducts(list[0].configuration === 'FINISHED');
        }
        setSaleItems(list);
    }, [items, skuItems, product, autoSelectSingle]);
    useEffect(() => {
        var _a;
        (_a = props.onSelectionChange) === null || _a === void 0 ? void 0 : _a.call(props, selectedItems.map((item) => item.id));
    }, [selectedItems]);
    const className = classNames('section product-section');
    const backgroundImage = (_a = options.backgroundImage) !== null && _a !== void 0 ? _a : null;
    return (React.createElement("div", { className: className, style: {
            backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none'
        } },
        options.selectionMode !== 'single' && (React.createElement("h2", { className: backgroundImage ? 'product-section__title--white' : '' }, t('Make your selection'))),
        options.selectionMode === 'single' && !product && (React.createElement("h2", { className: backgroundImage ? 'product-section__title--white' : '' }, t('Make your selection'))),
        React.createElement("div", { className: "product-section__content", "aria-busy": !ready }, ready
            ? saleItems.map((saleItem) => {
                var _a;
                return (React.createElement(SaleItemCard, { item: saleItem, variant: (_a = variantOf(saleItem.id)) !== null && _a !== void 0 ? _a : undefined, key: saleItem.id, onClick: onSaleItemCardClick, selected: selectedItems.some((item) => item.id === saleItem.id), lang: lang, hasDirectSales: hasDirectSales }));
            })
            : SKELETON_KEYS.map((key) => (React.createElement(StoreSaleItemCardSkeleton, { key: key, variant: "form" })))),
        React.createElement(FormInput, { type: "hidden", name: "saleItems", value: selectedItems
                .map((item) => `${item.id}:${item.quantity}` +
                (item.variant ? `:${item.variant}` : ''))
                .join(',') })));
}
